import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, AppError } from '@/contexts/AuthContext';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { useGoogleClientId } from '@/hooks/useGoogleClientId';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Mail, MessageSquare } from 'lucide-react';
import { AuthShell } from '@/components/auth/AuthShell';
import { AuthHeading, EditorialField, MonoLabel, PillButton } from '@/components/editorial';
import { homeFor } from '@/lib/home-route';
import { api } from '@/services/api';

// Built per render locale (#2109 L6): the validation messages are copy, so
// the schema takes the bound `t` rather than holding English literals.
const makeLoginSchema = (t: TFunction<'auth'>) =>
  z.object({
    email: z.string().email(t('shared.invalidEmail')),
    password: z.string().min(1, t('login.passwordRequired')),
    rememberMe: z.boolean().default(true),
  });

// @hookform/resolvers 5+ types the form's input and its validated output
// apart: `rememberMe` may be absent going in (`.default(true)`) but is always
// a boolean coming out, which is what onSubmit receives.
type LoginSchema = ReturnType<typeof makeLoginSchema>;
type LoginFormInput = z.input<LoginSchema>;
type LoginFormData = z.output<LoginSchema>;

/**
 * A welcome-back line that is read in the language the page has NOW (#2753).
 * The toast is raised the moment sign-in succeeds, a beat before the page
 * switches to the account's language; a string resolved by the sign-in
 * page's own `t` is fixed at that moment and read English on a Spanish
 * account while the page turned Spanish around it. This resolves at render,
 * so it follows the switch (react-i18next re-renders it on languageChanged).
 */
function WelcomeBackText({ line }: { line: 'title' | 'description' }) {
  const { t } = useTranslation('auth');
  return <>{line === 'title' ? t('login.welcomeBackTitle') : t('login.welcomeBackDescription')}</>;
}

export function LoginPage() {
  // B-168: like /apply (GH-271.1), this page only READS auth state and calls
  // the existing logout() action — no token paths, no AuthContext changes.
  const { login, loginWithSmsCode, loginWithGoogle, user, loading, logout } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('auth');
  const loginSchema = useMemo(() => makeLoginSchema(t), [t]);
  const navigate = useNavigate();
  // #2429: RequireAuth says when a live session ended under this person.
  const sessionExpired = (useLocation().state as { reason?: string } | null)?.reason === 'expired';
  const googleClientId = useGoogleClientId();
  const [switching, setSwitching] = useState(false);
  // One-way latch: once the credentials form has rendered on this mount, it
  // stays. AuthContext.login() sets `user` a beat before navigate(homeFor(...))
  // (a /me round trip sits between them), and that beat must not flash the
  // already-signed-in interstitial over a form the user just submitted.
  const formShownRef = useRef(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [smsRequesting, setSmsRequesting] = useState(false);
  const [smsCodePending, setSmsCodePending] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [smsVerifying, setSmsVerifying] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInput, unknown, LoginFormData>({
    resolver: zodResolver(loginSchema),
    // Remember-me defaults CHECKED (#763, DECISIONS 2026-08-13): the unticked
    // box silently downgraded users to a browser-session refresh cookie and a
    // full login + MFA on every browser restart — friction nobody chose.
    defaultValues: { rememberMe: true },
  });

  const rememberMe = watch('rememberMe');

  const handleRequestSmsCode = async () => {
    const email = getValues('email');
    const parsed = z.string().email().safeParse(email);
    if (!parsed.success) {
      toast({
        variant: 'destructive',
        title: t('login.enterEmailFirstTitle'),
        description: t('login.enterEmailFirstCode'),
      });
      return;
    }
    setSmsRequesting(true);
    try {
      await api.auth.requestSmsCode(email);
      setSmsCodePending(true);
      toast({
        title: t('login.codeSentTitle'),
        description: t('login.codeSentDescription'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('login.codeSendFailedTitle'),
        description: t('login.codeSendFailedDescription'),
      });
    } finally {
      setSmsRequesting(false);
    }
  };

  const handleVerifySmsCode = async () => {
    const email = getValues('email');
    const trimmed = smsCode.trim();
    if (trimmed.length < 4) {
      toast({ variant: 'destructive', title: t('login.enterCodeFromPhone') });
      return;
    }
    setSmsVerifying(true);
    try {
      await loginWithSmsCode(email, trimmed, rememberMe);
      toast({ title: <WelcomeBackText line="title" /> });
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : t('login.invalidCode');
      toast({ variant: 'destructive', title: t('login.signInFailedTitle'), description: msg });
    } finally {
      setSmsVerifying(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    try {
      await loginWithGoogle(credential, rememberMe);
    } catch (error: unknown) {
      const code = error instanceof AppError ? error.code : undefined;
      // Pending, rejected and role-disabled accounts come back as plain 403
      // sentences from the backend (no code), the same ones /login shows.
      // Show them as the password form does; never show a code.
      const message = error instanceof Error ? error.message : '';
      const accountRefusal =
        !code && /awaiting admin approval|registration was not approved|account is not active|currently unavailable/i.test(message);
      toast({
        variant: 'destructive',
        title: t('login.googleFailedTitle'),
        description: accountRefusal
          ? message
          : code === 'google_no_account' ? t('login.googleNoAccount') : t('login.googleFailed'),
      });
    }
  };

  const handleSendMagicLink = async () => {
    const email = getValues('email');
    const parsed = z.string().email().safeParse(email);
    if (!parsed.success) {
      toast({
        variant: 'destructive',
        title: t('login.enterEmailFirstTitle'),
        description: t('login.enterEmailFirstLink'),
      });
      return;
    }
    setMagicLinkSending(true);
    try {
      await api.auth.requestMagicLink(email);
      setMagicLinkSent(true);
      toast({
        title: t('login.linkSentTitle'),
        description: t('login.linkSentDescription', { email }),
      });
    } catch {
      // Backend always returns 200 for anti-enumeration; a thrown error here
      // means a network/5xx failure, so surface that distinctly.
      toast({
        variant: 'destructive',
        title: t('login.linkSendFailedTitle'),
        description: t('login.linkSendFailedDescription'),
      });
    } finally {
      setMagicLinkSending(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data, data.rememberMe);
      toast({
        title: <WelcomeBackText line="title" />,
        description: <WelcomeBackText line="description" />,
      });
    } catch (error: unknown) {
      const attemptsRemaining = error instanceof AppError ? error.attemptsRemaining : undefined;
      const base = error instanceof Error ? error.message : t('login.invalidEmailOrPassword');

      // Pending and rejected accounts return a specific message from the backend;
      // surface them as informational rather than destructive so the copy reads naturally.
      if (/awaiting admin approval/i.test(base)) {
        toast({
          title: t('login.awaitingApprovalTitle'),
          description: base,
        });
        return;
      }
      if (/registration was not approved/i.test(base)) {
        toast({
          variant: 'destructive',
          title: t('login.registrationNotApprovedTitle'),
          description: base,
        });
        return;
      }

      const warning =
        attemptsRemaining !== undefined && attemptsRemaining > 0 && attemptsRemaining <= 2
          ? t('login.attemptsWarning', { count: attemptsRemaining })
          : '';
      toast({
        variant: 'destructive',
        title: t('login.loginFailedTitle'),
        description: warning ? `${base} ${warning}` : base,
      });
    }
  };

  // B-168: a signed-in user who clicks a "Sign in" affordance must see their
  // session acknowledged, never a blank credentials form implying they're
  // signed out (B-164 naive-user QA). While auth state hydrates, show a
  // neutral spinner so neither the form nor the interstitial flashes early.
  // The formShownRef latch above keeps the form up through login transitions.
  if (!formShownRef.current) {
    if (loading) {
      return (
        <div
          className="flex min-h-screen items-center justify-center"
          role="status"
          aria-label={t('shared.checkingSession')}
        >
          <Loader2 className="h-6 w-6 animate-spin text-stevens-indigo" />
        </div>
      );
    }

    if (user) {
      const handleSwitchAccount = async () => {
        setSwitching(true);
        try {
          // logout() ends on /login with `user` null — this same mounted page
          // then falls through to the credentials form, ready for the next
          // sign-in. (While impersonating, logout() only exits impersonation,
          // so the interstitial re-renders with the admin's own identity.)
          await logout();
        } finally {
          setSwitching(false);
        }
      };

      return (
        <AuthShell layout="centered" documentLabel={t('login.documentLabel')}>
          <header className="flex flex-col gap-4 mb-10">
            <MonoLabel>{t('shared.alreadySignedIn')}</MonoLabel>
            <AuthHeading>
              {t('shared.alreadySignedInTitle')}
            </AuthHeading>
            <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
              <Trans
                t={t}
                i18nKey="login.alreadySignedInLede"
                values={{ email: user.email ?? t('shared.yourAccount') }}
                components={{ strong: <strong className="font-medium text-stevens-ink" /> }}
              />
            </p>
          </header>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <PillButton type="button" onClick={() => navigate(homeFor(user.role), { replace: true })}>
              {t('shared.goToMyAccount')} {"→"}
            </PillButton>
            <PillButton
              type="button"
              variant="ghost"
              onClick={handleSwitchAccount}
              disabled={switching}
            >
              {switching && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('login.signInAsSomeoneElse')}
            </PillButton>
          </div>
        </AuthShell>
      );
    }
  }
  formShownRef.current = true;

  // The sign-in page is one centered column with every way in visible at
  // once (Victor 2026-10-21, after Slack's sign-in): the password form, then
  // "or", then Google and the two passwordless buttons, each as wide as the
  // column. No aside, no Sign in or Apply in the top bar; Apply is one quiet
  // line at the foot of the column.
  const secondaryButton =
    'inline-flex h-11 w-full items-center justify-center gap-2 rounded-full border border-stevens-rule px-4 text-body font-medium text-stevens-ink hover:bg-stevens-bg-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <AuthShell
      layout="centered"
      documentLabel={t('login.documentLabel')}
      notice={
        // #2537 — the notice leads the page so a parent at 390×640 reads why
        // she was signed out before the form.
        sessionExpired ? (
          <p
            role="status"
            className="rounded-md border border-stevens-rule bg-stevens-bg-2 px-4 py-3 text-body leading-[1.6] text-stevens-ink m-0"
          >
            {t('login.sessionExpired')}
          </p>
        ) : undefined
      }
    >
      <header className="flex flex-col items-center gap-3 mb-8 text-center">
        <MonoLabel>{t('login.eyebrow')}</MonoLabel>
        <AuthHeading size="sm">
          <Trans t={t} i18nKey="login.title" components={{ em: <em className="not-italic text-stevens-indigo" /> }} />
        </AuthHeading>
        <p className="text-body leading-[1.6] text-stevens-ink-2 m-0">
          {t('login.lede')}
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        <EditorialField
          label={t('shared.emailLabel')}
          type="email"
          autoComplete="email"
          placeholder={t('shared.emailPlaceholder')}
          error={errors.email?.message}
          {...register('email')}
        />

        <EditorialField
          label={t('shared.passwordLabel')}
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          error={errors.password?.message}
          trailing={
            <Link
              to="/forgot-password"
              className="font-sans text-body-sm text-stevens-ink-2 hover:text-stevens-indigo transition-colors"
            >
              {t('login.forgotPassword')}
            </Link>
          }
          {...register('password')}
        />

        <label className="flex items-center gap-2.5 select-none cursor-pointer">
          <input
            id="rememberMe"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setValue('rememberMe', e.target.checked)}
            className="h-4 w-4 rounded border-stevens-rule text-stevens-indigo focus:ring-2 focus:ring-stevens-indigo/30 focus:ring-offset-0"
          />
          <span className="text-body text-stevens-ink-2">{t('login.rememberMe')}</span>
        </label>

        <PillButton type="submit" disabled={isSubmitting} className="w-full justify-center">
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('login.signingIn')}
            </>
          ) : (
            <>{t('login.signIn')} {"→"}</>
          )}
        </PillButton>
      </form>

      <div className="my-7 flex items-center gap-4" role="separator">
        <span className="h-px flex-1 bg-stevens-rule" aria-hidden="true" />
        <span className="text-body-sm text-stevens-ink-3">{t('login.or')}</span>
        <span className="h-px flex-1 bg-stevens-rule" aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-3">
        {/* #2324 — Google sign-in, only when this environment offers it (and
            never on native; useGoogleClientId returns null there). */}
        {googleClientId && (
          <GoogleSignInButton clientId={googleClientId} onCredential={handleGoogleCredential} fullWidth />
        )}

        {/* Passwordless — email magic link OR Twilio Verify SMS code. Both
            use the email field above; SMS additionally looks up the phone
            on the user's profile (silent no-op if none on file, for
            anti-enumeration). */}
        {!smsCodePending && (
          <>
            <button
              type="button"
              onClick={handleSendMagicLink}
              disabled={magicLinkSending || magicLinkSent}
              className={secondaryButton}
            >
              {magicLinkSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {magicLinkSent ? t('login.linkSent') : t('login.emailMeALink')}
            </button>
            <button
              type="button"
              onClick={handleRequestSmsCode}
              disabled={smsRequesting}
              className={secondaryButton}
            >
              {smsRequesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
              {t('login.textMeACode')}
            </button>
          </>
        )}

        {smsCodePending && (
          <div className="flex items-center gap-3">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/\s/g, ''))}
              placeholder={t('login.codePlaceholder')}
              aria-label={t('login.codePlaceholder')}
              className="min-w-0 flex-1 rounded-md border border-stevens-rule bg-transparent px-3 py-2 text-lede font-mono tracking-[0.2em] text-stevens-ink placeholder:text-stevens-ink-placeholder focus:outline-none focus:ring-2 focus:ring-stevens-indigo/30"
            />
            <button
              type="button"
              onClick={handleVerifySmsCode}
              disabled={smsVerifying || smsCode.trim().length < 4}
              className="inline-flex h-11 items-center gap-1.5 rounded-full bg-stevens-ink px-4 text-body-sm font-medium text-stevens-bg hover:bg-stevens-ink/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {smsVerifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {t('login.verify')} {"→"}
            </button>
            <button
              type="button"
              onClick={() => {
                setSmsCodePending(false);
                setSmsCode('');
              }}
              className="font-sans text-body-sm text-stevens-ink-2 hover:text-stevens-ink"
            >
              {t('login.codeCancel')}
            </button>
          </div>
        )}

        <p className="text-body-sm text-stevens-ink-2 text-center m-0" aria-live="polite">
          {magicLinkSent
            ? t('login.passwordlessLinkSent')
            : smsCodePending
            ? t('login.passwordlessCodeSent')
            : t('login.passwordlessHint')}
        </p>
      </div>

      <p className="mt-10 flex items-center justify-center gap-1.5 text-body text-stevens-ink-2 m-0">
        <span>{t('login.alternatePrompt')}</span>
        <Link
          to="/apply"
          className="text-stevens-ink hover:text-stevens-indigo border-b border-stevens-rule pb-0.5 transition-colors"
        >
          {t('login.alternateLabel')}
        </Link>
      </p>
    </AuthShell>
  );
}
